import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/Spinner';
import { getKycAccessibleStepIndex, useKycStatus } from '@/hooks/useKyc';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Si true, redirige si KYC non validé */
  requireKyc?: boolean;
  /** Si true, redirige si wallet non actif */
  requireWallet?: boolean;
}

export function ProtectedRoute({
  children,
  requireKyc    = false,
  requireWallet = false,
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ink-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/connexion" state={{ from: location }} replace />;
  }

  if (user?.accountState === 'pending') {
    return <Navigate to="/verifier-email" replace />;
  }

  if (requireKyc && user?.kycStatus !== 'validated') {
    const status = user?.kycStatus;
    if (status === 'submitted' || status === 'under_review') {
      // KYC en cours de traitement → permettre la création wallet en attendant
      return <Navigate to="/wallet/creation" state={{ from: location }} replace />;
    }
    // not_started, in_progress, rejected ou inconnu → tunnel KYC
    return <Navigate to="/kyc/informations-personnelles" state={{ from: location }} replace />;
  }

  if (requireWallet && user?.walletState !== 'active') {
    return <Navigate to="/wallet/creation" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Garde frontend d'expérience utilisateur pour les pages admin.
 * La décision d'autorisation réelle reste portée par le backend via /auth/me.isAdmin
 * et les middlewares serveur requireAuth/requireAdmin.
 */
export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ink-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/connexion" state={{ from: location }} replace />;
  }

  if (!user?.isAdmin) {
    return <Navigate to="/tableau-de-bord" replace />;
  }

  return <>{children}</>;
}

/**
 * Redirige les utilisateurs déjà connectés (login/register)
 * vers le dashboard.
 */
export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ink-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Une ancienne session pending ne doit pas empêcher la connexion avec un
    // autre compte déjà actif (mot de passe ou Google).
    if (user?.accountState === 'pending' && location.pathname === '/connexion') {
      return <>{children}</>;
    }
    return <Navigate to="/tableau-de-bord" replace />;
  }

  return <>{children}</>;
}

export function KycStepRoute({
  stepIndex,
  children,
}: {
  stepIndex: number;
  children: React.ReactNode;
}) {
  const { data: kycStatus, isLoading } = useKycStatus();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ink-50">
        <Spinner size="lg" />
      </div>
    );
  }

  const maxAccessibleIndex = getKycAccessibleStepIndex(kycStatus?.progress);
  if (stepIndex > maxAccessibleIndex) {
    const routes = [
      '/kyc/informations-personnelles',
      '/kyc/identite',
      '/kyc/selfie',
      '/kyc/justificatif-domicile',
      '/kyc/source-des-fonds',
      '/kyc/revue',
    ];
    return <Navigate to={routes[maxAccessibleIndex]} replace />;
  }

  return <>{children}</>;
}
